import { useCallback, useEffect, useRef, useState } from 'react'

export type PollFetcher = (signal: AbortSignal) => Promise<void>

interface UsePollingResult {
  refresh: () => void
  isFetching: boolean
  lastUpdatedAt: number | null
  error: unknown
}

const REQUEST_TIMEOUT_MS = 30000

const isAbortError = (error: unknown): boolean => {
  if (typeof error !== 'object' || error === null) return false
  const candidate = error as { name?: string; code?: string }
  return (
    candidate.name === 'AbortError' ||
    candidate.name === 'CanceledError' ||
    candidate.code === 'ERR_CANCELED'
  )
}

export function usePolling(
  fetcher: PollFetcher,
  intervalMs: number,
  enabled = true
): UsePollingResult {
  const [isFetching, setIsFetching] = useState(false)
  const [lastUpdatedAt, setLastUpdatedAt] = useState<number | null>(null)
  const [error, setError] = useState<unknown>(null)
  const [resetKey, setResetKey] = useState(0)

  const fetcherRef = useRef(fetcher)
  fetcherRef.current = fetcher

  const abortRef = useRef<AbortController | null>(null)

  const run = useCallback(async () => {
    abortRef.current?.abort()
    const controller = new AbortController()
    abortRef.current = controller
    setIsFetching(true)

    let timedOut = false
    const watchdog = window.setTimeout(() => {
      timedOut = true
      controller.abort()
    }, REQUEST_TIMEOUT_MS)

    try {
      await fetcherRef.current(controller.signal)
      if (!controller.signal.aborted) {
        setLastUpdatedAt(Date.now())
        setError(null)
      }
    } catch (caught) {
      if (timedOut || !isAbortError(caught)) setError(caught)
    } finally {
      clearTimeout(watchdog)
      if (abortRef.current === controller) {
        abortRef.current = null
        setIsFetching(false)
      }
    }
  }, [])

  useEffect(() => {
    if (!enabled) return

    run()
    const id = intervalMs > 0 ? window.setInterval(run, intervalMs) : undefined

    return () => {
      if (id !== undefined) clearInterval(id)
      abortRef.current?.abort()
    }
  }, [enabled, intervalMs, resetKey, run])

  const refresh = useCallback(() => setResetKey((key) => key + 1), [])

  return { refresh, isFetching, lastUpdatedAt, error }
}
